import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type {
  Product,
  Category,
  Brand,
  Store,
  RecommendationCategory,
  UseCase,
  FilterOptions,
  ActiveFilters,
  PaginatedResponse,
  SearchResult,
  Review,
  RecommendedProduct,
  PopularSpec,
  BrandStats,
  TrendingSpec,
  PCBuild,
  NaverSearchResponse,
  PriceComparisonResponse,
  TrendingKeywordsResponse,
} from '../types';

// API Base Configuration
// Use Worker API for all endpoints (Pages Functions incomplete)
const API_BASE = 'https://moapc-worker.linuxm.workers.dev/api';

// API Client
async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: 'An error occurred while fetching data',
    }));
    throw new Error(error.message || 'Failed to fetch data');
  }

  return response.json();
}

// ===== PRODUCTS =====
export function useProducts(
  filters?: ActiveFilters,
  page = 1,
  limit = 20,
  options?: Omit<UseQueryOptions<PaginatedResponse<Product>>, 'queryKey' | 'queryFn'>
) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (filters?.categoryIds.length) {
    params.append('categories', filters.categoryIds.join(','));
  }
  if (filters?.brandIds.length) {
    params.append('brands', filters.brandIds.join(','));
  }
  if (filters?.priceRange) {
    params.append('minPrice', String(filters.priceRange.min));
    params.append('maxPrice', String(filters.priceRange.max));
  }
  if (filters?.searchQuery) {
    params.append('search', filters.searchQuery);
  }
  if (filters?.inStockOnly) {
    params.append('inStock', 'true');
  }

  return useQuery({
    queryKey: ['products', filters, page, limit],
    queryFn: () => apiFetch<PaginatedResponse<Product>>(`/products?${params}`),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

export function useProduct(
  id: string,
  options?: Omit<UseQueryOptions<Product>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['product', id],
    queryFn: () => apiFetch<Product>(`/products/${id}`),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  });
}

export function useFeaturedProducts(
  limit = 8,
  options?: Omit<UseQueryOptions<Product[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['featured-products', limit],
    queryFn: () => apiFetch<Product[]>(`/products/featured?limit=${limit}`),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  });
}

// ===== CATEGORIES =====
export function useCategories(
  options?: Omit<UseQueryOptions<Category[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => apiFetch<Category[]>('/categories'),
    ...options,
  });
}

export function useCategory(
  slug: string,
  options?: Omit<UseQueryOptions<Category>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['category', slug],
    queryFn: () => apiFetch<Category>(`/categories/${slug}`),
    enabled: !!slug,
    ...options,
  });
}

// ===== BRANDS =====
export function useBrands(
  options?: Omit<UseQueryOptions<Brand[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['brands'],
    queryFn: () => apiFetch<Brand[]>('/brands'),
    ...options,
  });
}

// ===== STORES =====
export function useStores(
  options?: Omit<UseQueryOptions<Store[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
    ...options,
  });
}

// ===== RECOMMENDATIONS =====
export function useRecommendations(
  options?: Omit<UseQueryOptions<RecommendationCategory[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['recommendations'],
    queryFn: () => apiFetch<RecommendationCategory[]>('/recommendations'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  });
}

export function useRecommendationCategory(
  slug: string,
  options?: Omit<UseQueryOptions<RecommendationCategory>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['recommendation', slug],
    queryFn: () => apiFetch<RecommendationCategory>(`/recommendations/${slug}`),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  });
}

export function useUseCases(
  options?: Omit<UseQueryOptions<UseCase[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['use-cases'],
    queryFn: () => apiFetch<UseCase[]>('/use-cases'),
    ...options,
  });
}

export function useUseCaseRecommendations(
  useCaseId: string,
  options?: Omit<UseQueryOptions<RecommendedProduct[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['use-case-recommendations', useCaseId],
    queryFn: () => apiFetch<RecommendedProduct[]>(`/use-cases/${useCaseId}/recommendations`),
    enabled: !!useCaseId,
    ...options,
  });
}

// ===== SEARCH =====
export function useSearch(
  query: string,
  options?: Omit<UseQueryOptions<SearchResult>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['search', query],
    queryFn: () => apiFetch<SearchResult>(`/search?q=${encodeURIComponent(query)}`),
    enabled: query.length >= 2,
    ...options,
  });
}

// ===== FILTERS =====
export function useFilterOptions(
  options?: Omit<UseQueryOptions<FilterOptions>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['filter-options'],
    queryFn: () => apiFetch<FilterOptions>('/filters/options'),
    ...options,
  });
}

// ===== REVIEWS =====
export function useProductReviews(
  productId: string,
  page = 1,
  limit = 10,
  options?: Omit<UseQueryOptions<PaginatedResponse<Review>>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['reviews', productId, page, limit],
    queryFn: () =>
      apiFetch<PaginatedResponse<Review>>(
        `/products/${productId}/reviews?page=${page}&limit=${limit}`
      ),
    enabled: !!productId,
    ...options,
  });
}

// ===== MUTATIONS =====
interface VoteReviewParams {
  reviewId: string;
  helpful: boolean;
}

export function useVoteReview(
  options?: UseMutationOptions<void, Error, VoteReviewParams>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reviewId, helpful }) =>
      apiFetch<void>(`/reviews/${reviewId}/vote`, {
        method: 'POST',
        body: JSON.stringify({ helpful }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },
    ...options,
  });
}

// ===== HOME PAGE =====
export function usePopularSpecs(
  options?: Omit<UseQueryOptions<PopularSpec[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['popular-specs'],
    queryFn: () => apiFetch<PopularSpec[]>('/specs/popular'),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  });
}

export function useBrandStats(
  options?: Omit<UseQueryOptions<BrandStats[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['brand-stats'],
    queryFn: () => apiFetch<BrandStats[]>('/brands/stats'),
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    ...options,
  });
}

export function useTrendingSpecs(
  options?: Omit<UseQueryOptions<TrendingSpec[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['trending-specs'],
    queryFn: () => apiFetch<TrendingSpec[]>('/specs/trending'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  });
}

export function usePCBuilds(
  type: 'gaming' | 'office' | 'creative' | 'budget',
  options?: Omit<UseQueryOptions<PCBuild[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['builds', type],
    queryFn: () => apiFetch<PCBuild[]>(`/builds/${type}`),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    ...options,
  });
}

// ===== NAVER SHOPPING =====
export function useNaverSearch(
  query: string,
  display = 10,
  sort = 'sim',
  options?: Omit<UseQueryOptions<NaverSearchResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['naver-search', query, display, sort],
    queryFn: () =>
      apiFetch<NaverSearchResponse>(
        `/naver/search?query=${encodeURIComponent(query)}&display=${display}&sort=${sort}`
      ),
    enabled: query.length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

export function useProductPriceComparison(
  productId: string,
  options?: Omit<UseQueryOptions<PriceComparisonResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['price-comparison', productId],
    queryFn: () => apiFetch<PriceComparisonResponse>(`/products/${productId}/compare`),
    enabled: !!productId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  });
}

export function useTrendingKeywords(
  limit = 5,
  options?: Omit<UseQueryOptions<TrendingKeywordsResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['trending-keywords', limit],
    queryFn: () => apiFetch<TrendingKeywordsResponse>(`/trends/it?limit=${limit}`),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    ...options,
  });
}

// ===== PRICE DROPS =====
export interface PriceDrop {
  product_id: string;
  name: string;
  category: string;
  current_price: number;
  previous_price: number;
  drop_percent: number;
  drop_amount: number;
  slug?: string;
}

export function usePriceDrops(
  days = 7,
  minDrop = 5,
  limit = 6,
  options?: Omit<UseQueryOptions<PriceDrop[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['price-drops', days, minDrop, limit],
    queryFn: async () => {
      const response = await apiFetch<{ products: PriceDrop[] }>(`/price-drops?days=${days}&minDrop=${minDrop}&limit=${limit}`);
      return response.products;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  });
}

// ===== LOWEST PRICES =====
export interface LowestPriceItem {
  category: string;
  categorySlug: string;
  product_id: string;
  name: string;
  price: number;
  store: string;
  url?: string;
  slug?: string;
}

export function useLowestPrices(
  options?: Omit<UseQueryOptions<LowestPriceItem[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['lowest-prices'],
    queryFn: async () => {
      const response = await apiFetch<{ products: LowestPriceItem[] }>('/lowest-prices');
      return response.products;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    ...options,
  });
}

// ===== QUERY KEYS =====
export const queryKeys = {
  products: ['products'] as const,
  product: (id: string) => ['product', id] as const,
  categories: ['categories'] as const,
  category: (slug: string) => ['category', slug] as const,
  brands: ['brands'] as const,
  stores: ['stores'] as const,
  recommendations: ['recommendations'] as const,
  recommendation: (slug: string) => ['recommendation', slug] as const,
  useCases: ['use-cases'] as const,
  search: (query: string) => ['search', query] as const,
  reviews: (productId: string) => ['reviews', productId] as const,
  popularSpecs: ['popular-specs'] as const,
  brandStats: ['brand-stats'] as const,
  trendingSpecs: ['trending-specs'] as const,
  pcBuilds: (type: string) => ['builds', type] as const,
  lowestPrices: ['lowest-prices'] as const,
};
